import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

interface SessionTrendChartProps {
  data: Array<{
    date: string;
    sessions: number;
  }>;
  loading?: boolean;
  dateRange?: number;
}

const chartConfig = {
  sessions: {
    label: "Sessions",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

export function SessionTrendChart({
  data,
  loading = false,
  dateRange = 30,
}: SessionTrendChartProps) {
  // Format the data for display based on date range
  const chartData = data.map((item) => {
    let date: Date;

    // For 24-hour view, parse as ISO datetime
    if (dateRange === 1) {
      date = new Date(item.date);
      return {
        date: date.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        }),
        fullDate: item.date,
        sessions: item.sessions,
      };
    }

    // For other views, parse date string in local timezone
    // Split date string and create date at midnight in local time
    const [year, month, day] = item.date.split("-");
    date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      fullDate: item.date,
      sessions: item.sessions,
    };
  });

  const getChartTitle = () => {
    if (dateRange === 1) return "Session Trend (Last 24 Hours)";
    if (dateRange === 3) return "Session Trend (Last 3 Days)";
    if (dateRange === 7) return "Session Trend (Last 7 Days)";
    if (dateRange === 30) return "Session Trend (Last 30 Days)";
    if (dateRange === 365) return "Session Trend (Last Year)";
    return `Session Trend (Last ${dateRange} Days)`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{getChartTitle()}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[300px] w-full bg-muted animate-pulse rounded" />
        ) : (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <AreaChart data={chartData} accessibilityLayer>
              <defs>
                <linearGradient id="fillSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="var(--color-sessions)"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--color-sessions)"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={dateRange === 1 ? 20 : 32}
                angle={dateRange === 1 ? -45 : 0}
                textAnchor={dateRange === 1 ? "end" : "middle"}
                height={dateRange === 1 ? 60 : 30}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(value, payload) => {
                      if (payload && payload[0]) {
                        let date: Date;
                        const fullDate = payload[0].payload.fullDate;

                        // For 24-hour view, parse as ISO datetime
                        if (dateRange === 1) {
                          date = new Date(fullDate);
                        } else {
                          // For other views, parse date string in local timezone
                          const [year, month, day] = fullDate.split("-");
                          date = new Date(
                            parseInt(year),
                            parseInt(month) - 1,
                            parseInt(day)
                          );
                        }

                        // For 24-hour view, show date and time
                        if (dateRange === 1) {
                          return date.toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          });
                        }

                        // For other views, show full date
                        return date.toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        });
                      }
                      return value;
                    }}
                  />
                }
              />
              <Area
                dataKey="sessions"
                type="monotone"
                fill="url(#fillSessions)"
                fillOpacity={0.4}
                stroke="var(--color-sessions)"
                strokeWidth={2}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
